import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Cable, TriangleAlert } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import { useFlightData, useIntegrations, useProviderRouting } from '@/lib/providers/use-providers';
import { unknownEntries } from '@/lib/providers/integration-state';
import {
  attentionItems,
  buildSections,
  filterCounts,
  flightFacts,
  flightRowState,
  FLIGHT_INFO_SECTION,
  matchesFilter,
  nextFilter,
  vendorAccounts,
  type ProviderFilter,
} from '@/lib/providers/providers-overview';
import type { ProviderStatus } from '@/lib/providers/types';
import type { IntegrationsView } from '@/lib/providers/integrations-view';
import { AIRLINE_PROVIDER_VENDOR } from '@/lib/providers/airline-provider';
import { AirlineProviderCard } from './airline-provider-card';
import { UnknownDeclarantCard } from './integrations-card';
import { IntegrationsDiagram } from './integrations-diagram';
import { FlightInfoSection } from './flight-info';
import { AttentionLine, EmptyFilter, FilterPills } from './providers-summary';
import { ServiceSection } from './service-section';
import { VendorAccounts } from './vendor-accounts';

/**
 * "What does this environment use for each service, in which mode, and does it
 * answer?" — answered PER SERVICE, all on one page.
 *
 * The instance declares itself: every app publishes the integrations it
 * actually BUILT at boot, `apps/integrations` aggregates them, the operator-bff
 * proxies the answer AND computes each row's availability on that same read
 * (probe cache, real traffic, breaker). This section renders it as:
 *
 *   1. five filter pills — all · real · sandbox · synthetic · off or not
 *      configured — that are also the environment's summary;
 *   2. ONE attention line, only when something needs looking at;
 *   3. the declarants we could not ask (a different component, never a row);
 *   4. M2 — the vendor ACCOUNTS that serve several services, once each — and
 *      the AIRLINE PROVIDER's own card (`airline-air-ticketing.v1` §0): one
 *      provider, its master switch, a switch per service and what each gives;
 *   5. one card per SERVICE, stacked: flight information, passenger data, air
 *      tickets, hotels, transport, notifications, payments, then anything else.
 *
 * The diagram view (the operator's choice, `view`) replaces 3–4 with the
 * connection diagram; flight information stays above it.
 *
 * ── REALTIME: ONE READ, ONE BOUNDED CATCH-UP, THEN THE WS ──────────────────────
 * One read on mount. Rows whose check was still running come back `checking`;
 * only then the hook reads again a few seconds later (at most twice) to pick
 * the result up — see `useIntegrations`. A switch, mode or order changed by
 * anyone arrives as `IntegrationChanged` in the `providers` room (joined by the
 * page) and re-reads once. No poll.
 *
 * ── ALL FOUR STATES ──────────────────────────────────────────────────────────
 * loading (skeleton blocks) · error (the read itself failed) · empty (nothing
 * was declared) · success. The empty copy is deliberately careful: the BFF is
 * fail-soft and answers an unreachable inventory with an EMPTY body, so "no
 * integrations" and "we could not assemble the inventory" arrive identically.
 * Flight information comes from another upstream and renders in every state
 * but loading: a dead service degrades a card, it never takes down the page.
 */
export function IntegrationsSection({
  view = 'list',
  traffic,
}: {
  view?: IntegrationsView;
  /** The recent-traffic read, for the configured rate limits in a row's detail. */
  traffic?: ProviderStatus[] | undefined;
}) {
  const { t } = useTranslation('network');
  const { data, isLoading, isError } = useIntegrations();
  // §14.5 — the "Order of use" of each routed category. Its own read: when the
  // routing control plane cannot answer, the cards simply show their selection
  // policy and the rest renders exactly as before.
  const routing = useProviderRouting().data?.items ?? [];
  const flight = useFlightData();
  const flightState = flightRowState(flight.data, flight);
  const [filter, setFilter] = useState<ProviderFilter>('all');

  // The FlightAware row alone — the error, empty and diagram states.
  const flightCard = () => <FlightInfoSection state={flightState} data={flight.data} isLoading={flight.isLoading} />;

  if (isLoading) {
    return (
      <section className="space-y-3" data-testid="providers-integrations" aria-busy="true">
        <div className="space-y-3" data-testid="integrations-loading">
          <Skeleton className="h-16 w-full" />
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-40 w-full" />
          ))}
        </div>
      </section>
    );
  }

  if (isError || !data) {
    return (
      <section className="space-y-4" data-testid="providers-integrations">
        <EmptyState
          icon={<TriangleAlert className="h-8 w-8" aria-hidden />}
          title={t('providers.integrations.loadErrorTitle')}
          description={t('providers.integrations.loadErrorHint')}
          className="p-8"
        />
        <AirlineProviderCard />
        {flightCard()}
      </section>
    );
  }

  const unknown = unknownEntries(data);
  if (data.integrations.length === 0 && unknown.length === 0) {
    return (
      <section className="space-y-4" data-testid="providers-integrations">
        <EmptyState
          icon={<Cable className="h-8 w-8" aria-hidden />}
          title={t('providers.integrations.emptyTitle')}
          description={t('providers.integrations.emptyHint')}
          className="p-8"
        />
        {/* An empty inventory may be one we could not assemble: no switch is claimed from it. */}
        <AirlineProviderCard />
        {flightCard()}
      </section>
    );
  }

  const sections = buildSections(data.integrations, routing);
  const flightSection = sections.find((s) => s.category === FLIGHT_INFO_SECTION);
  const services = sections.filter((s) => s !== flightSection);
  // The airline provider says its services once, on its own card — not in the accounts strip.
  const accountList = vendorAccounts(data.integrations).filter((a) => a.vendor !== AIRLINE_PROVIDER_VENDOR);
  const accounts = new Map(accountList.map((a) => [a.vendor, a] as const));
  const flightRow = flightFacts(flightState);
  const facts = [flightRow, ...sections.flatMap((s) => s.rows.map((r) => r.facts))];
  const counts = filterCounts(facts);
  const attention = attentionItems({ integrations: data.integrations, unknown, flight: flightState });
  const anyVisible = facts.some((f) => matchesFilter(f, filter));

  return (
    <section className="space-y-4" data-testid="providers-integrations" data-filter={filter}>
      {view === 'list' ? (
        <FilterPills counts={counts} active={filter} onPick={(picked) => setFilter((cur) => nextFilter(cur, picked))} />
      ) : null}
      <AttentionLine items={attention} />

      {view === 'diagram' ? (
        <>
          <AirlineProviderCard integrations={data.integrations} />
          {flightCard()}
          {/* The diagram draws the unknown declarants itself, in a lane of
              their own above the categories — same order, same precedence. */}
          <IntegrationsDiagram inventory={data} />
        </>
      ) : (
        <>
          {/* Unknown FIRST, and outside the service cards: a service we could not
              reach is the one thing here that might be an outage, and we do not
              know which service its integrations belong to — putting it in one
              would be a guess. It is not a provider, so no filter hides it. */}
          {unknown.length > 0 ? (
            <div className="grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-3" data-testid="integrations-unknown">
              {unknown.map((entry) => (
                <UnknownDeclarantCard key={entry.declaredBy} entry={entry} />
              ))}
            </div>
          ) : null}

          <VendorAccounts accounts={accountList} />

          {/* The airline provider (airline-air-ticketing.v1 §0): ONE provider, three services,
              one card with its master switch. Its rows stay in their service cards — the order
              of use and the counts need them there — and point back here for the switch. */}
          <AirlineProviderCard integrations={data.integrations} />

          <div className="space-y-4" data-testid="integrations-categories">
            <FlightInfoSection
              state={flightState}
              data={flight.data}
              isLoading={flight.isLoading}
              section={flightSection}
              filter={filter}
              traffic={traffic}
              accounts={accounts}
            />
            {services.map((section) => (
              <ServiceSection
                key={section.category}
                section={section}
                filter={filter}
                traffic={traffic}
                accounts={accounts}
              />
            ))}
            {anyVisible ? null : <EmptyFilter onClear={() => setFilter('all')} />}
          </div>
        </>
      )}
    </section>
  );
}
